import axiosClient from '../../axios'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'

/**
 * Admin-only hooks for piloting follow-up.
 * Back the new /admin/pilots page — read-only, lean payloads.
 */

export interface PilotSummary {
  id: string
  email: string
  contactName: string | null
  companyName: string | null
  plan: string | null
  signedUpAt: string
  lastActivity: string | null
  blotCases: { total: number; last30Days: number }
  /** Sum of visible (non-ghost) blots across all the pilot's cases. */
  totalBlots: number
  credits: { remaining: number; used: number; shared?: boolean } | null
}

export interface PilotDetail {
  user: {
    id: string
    email: string
    contactName: string | null
    companyName: string | null
    plan: string | null
    role: string
    createdAt: string
  }
  lastActivity: string | null
  cases: {
    blot: {
      total: number
      last30Days: number
      lastUpdatedAt: string | null
      lastSampleId: string | null
      lastPathology: string | null
    }
    malaria: {
      total: number
      last30Days: number
      lastUpdatedAt: string | null
    }
  }
  credits: {
    total: number
    remaining: number
    used: number
    consumedLast30Days: number
  } | null
  modules: Array<{ type: string; enabled: boolean; updatedAt: string }>
}

export interface PilotBlotCase {
  id: string
  sampleId: string | null
  filename: string | null
  /** Where the pipeline's own image lives — the plate as it was analysed, not
   *  the file that was uploaded. Already returned by /recent-cases. */
  fileUrl?: string | null
  pathologyName: string | null
  totalBlots: number | null
  totalBands: number | null
  /** True once every sample blot (controls/ghosts excluded) has a verdict. */
  validated?: boolean
  validatedCount?: number
  sampleTotal?: number
  /** Corrections manuelles du biologiste, telles que le cas les persiste :
   *  bandes placées ({ blotId: [{ name }] }), supprimées ({ blotId: [nom] })
   *  et blots repositionnés ({ blotId: compte }). */
  placedBands?: Record<string, Array<{ name: string }>> | null
  suppressedBands?: Record<string, string[]> | null
  repositionedBlots?: Record<string, number> | null
  createdAt: string
  updatedAt: string
}

/** Cross-pilot table-shaped roll-up (one row per non-admin user). */
export function usePilots() {
  return useQuery({
    queryKey: ['admin', 'pilots'],
    queryFn: async () => {
      const { data } = await axiosClient.get<{ count: number; pilots: PilotSummary[] }>(
        '/users/pilots/usage-summary',
      )
      return data
    },
    staleTime: 60_000,
  })
}

/** Detailed snapshot for a single pilot — drives the expanded row. */
export function usePilotDetail(userId: string | null) {
  return useQuery({
    queryKey: ['admin', 'pilot', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await axiosClient.get<PilotDetail>(`/users/${userId}/usage-summary`)
      return data
    },
    staleTime: 30_000,
  })
}

/** Blot cases owned by a specific pilot (admin shadow read). */
export function usePilotBlotCases(userId: string | null, limit = 50) {
  return useQuery({
    queryKey: ['admin', 'pilot', userId, 'blot-cases', limit],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await axiosClient.get<{ count: number; analyses: PilotBlotCase[] }>(
        '/blot-analysis/recent-cases',
        // light=1 : le tableau admin n'affiche jamais les blots eux-mêmes, et
        // sans la projection chaque cas revient avec ses images en base64.
        { params: { userId, limit, light: '1' } },
      )
      return data
    },
    staleTime: 30_000,
    // Bumping `limit` (the "charger plus" pagination) swaps the query key —
    // keep showing the previous page's rows instead of flashing empty while
    // the bigger page loads.
    placeholderData: keepPreviousData,
  })
}

/** Admin — grant more IA credits to a pilot (top up a tester). */
export function useSetPilotCredits() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ userId, add }: { userId: string; add: number }) => {
      const { data } = await axiosClient.patch<{ remaining: number }>(
        `/users/${userId}/credits`,
        { add },
      )
      return data
    },
    onSuccess: (_d, { userId }) => {
      qc.invalidateQueries({ queryKey: ['admin', 'pilots'] })
      qc.invalidateQueries({ queryKey: ['admin', 'pilot', userId] })
    },
  })
}

// ─── Label Studio re-training dataset export ─────────────────────────────────

export interface DatasetFacets {
  total: number
  /** Cases matching the filters passed to the hook (total when none). */
  matching: number
  pathologies: Array<{ id: string | null; name: string; count: number }>
  profiles: Array<{ profile: string; count: number }>
}

/** Distinct pathologies + detection profiles present in stored cases. */
export function useDatasetFacets(filters: DatasetExportFilters = {}) {
  return useQuery({
    queryKey: ['admin', 'blot-dataset', 'facets', filters],
    queryFn: async () => {
      const { data } = await axiosClient.get<DatasetFacets>('/blot-analysis/dataset/facets', {
        params: filters,
      })
      return data
    },
    staleTime: 60_000,
  })
}

export interface DatasetExportFilters {
  pathologyId?: string
  detectionProfile?: string
  userId?: string
  from?: string
  to?: string
}

/**
 * Download the Label Studio ZIP (raw scans + strip pre-annotations) for the
 * given filters, straight to the browser. Uses the authenticated axios client
 * so the admin-only endpoint accepts it.
 */
export async function downloadBlotDataset(filters: DatasetExportFilters): Promise<void> {
  const params = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== undefined && v !== ''),
  )
  const res = await axiosClient.get('/blot-analysis/dataset/export', {
    params,
    responseType: 'blob',
  })
  const blob = new Blob([res.data], { type: 'application/zip' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `careflai-blot-dataset-${new Date().toISOString().slice(0, 10)}.zip`
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}
